import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { TemplateVariable } from "../../../lib/schema-paths.js";

interface Props {
	variables: TemplateVariable[];
	filter: string;
	onSelect: (path: string) => void;
	onDismiss: () => void;
	position: { top: number; left: number };
}

export function TemplateDropdown({ variables, filter, onSelect, onDismiss, position }: Props) {
	const [activeIndex, setActiveIndex] = useState(0);
	const listRef = useRef<HTMLDivElement>(null);

	const filtered = variables.filter((v) => v.path.toLowerCase().includes(filter.toLowerCase()));

	// biome-ignore lint/correctness/useExhaustiveDependencies: filter is a prop, not an outer-scope value — biome false positive
	useEffect(() => {
		setActiveIndex(0);
	}, [filter]);

	const activeId = filtered[activeIndex] ? `tmpl-opt-${activeIndex}` : undefined;

	const handleKeyDown = (e: React.KeyboardEvent) => {
		switch (e.key) {
			case "ArrowDown":
				e.preventDefault();
				setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
				break;
			case "ArrowUp":
				e.preventDefault();
				setActiveIndex((i) => Math.max(i - 1, 0));
				break;
			case "Enter":
				e.preventDefault();
				if (filtered[activeIndex]) onSelect(filtered[activeIndex].path);
				break;
			case "Escape":
				e.preventDefault();
				onDismiss();
				break;
		}
	};

	const dropdown = (
		<div
			ref={listRef}
			// biome-ignore lint/a11y/useSemanticElements: custom ARIA listbox — native <select> is not suitable here
			role="listbox"
			aria-activedescendant={activeId}
			onKeyDown={handleKeyDown}
			tabIndex={-1}
			style={{ position: "fixed", top: position.top, left: position.left }}
			className="z-50 max-h-48 w-80 overflow-y-auto rounded-xl border border-border bg-surface shadow-lg"
		>
			<div className="border-b border-border px-2 py-1 text-xs text-text-muted">
				Available variables
			</div>
			{filtered.length === 0 && (
				<div className="px-2 py-2 text-xs text-text-muted">No matching variables</div>
			)}
			{filtered.map((v, i) => (
				<div
					key={v.path}
					id={`tmpl-opt-${i}`}
					// biome-ignore lint/a11y/useSemanticElements: custom ARIA option within listbox
					role="option"
					aria-selected={i === activeIndex}
					tabIndex={-1}
					onClick={() => onSelect(v.path)}
					onKeyDown={(e) => {
						if (e.key === "Enter") onSelect(v.path);
					}}
					className={`flex cursor-pointer items-center gap-2 px-2 py-1.5 font-mono text-xs ${
						i === activeIndex
							? "border-l-2 border-accent bg-accent-bg"
							: "border-l-2 border-transparent"
					}`}
				>
					<span className="flex-1 truncate text-text-primary">{v.path}</span>
					<span className="text-xs text-text-muted">{v.type}</span>
				</div>
			))}
		</div>
	);

	return createPortal(dropdown, document.body);
}
